// ppt-gen.docx.service.ts
import { Injectable } from "@angular/core";
// Import docx components
import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    Table,
    TableRow,
    TableCell,
    WidthType,
    VerticalAlign,
    AlignmentType,
    ExternalHyperlink,
    BorderStyle,
} from 'docx';
// Import file-saver for client-side file download
import { saveAs } from 'file-saver';
// Import common service for helper functions
import { PptGenCommonService } from './ppt-gen.common.service';

@Injectable({
    providedIn: 'root'
})
export class DocxGenService {

    constructor(private commonService: PptGenCommonService) { }

    /**
     * Generates a DOCX file based on vendor submission data.
     * @param submissionId The ID of the submission.
     * @param vendorName The name of the vendor.
     * @param sectionsData Data for the sections, including questions.
     * @param answersData Raw answer data from the API.
     */
    async generateVendorSubmissionDocx(
        submissionId: number,
        vendorName: any,
        sectionsData: any[],
        answersData: any[]
    ) {
        console.log("Generating DOCX...");

        const docChildren: (Paragraph | Table)[] = []; // Array to hold document content

        // --- Title Section ---
        docChildren.push(
            new Paragraph({
                children: [ // TextRun for styling
                    new TextRun({
                        text: 'Vendor Submission Details',
                        size: 50, // 25pt
                        bold: true,
                        color: "005691",
                    }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 } // Spacing in TWIP (1/20th of a point)
            }),
            new Paragraph({
                children: [
                    new TextRun({ text: `Submission ID: ${submissionId}`, bold: true, size: 32 }), // 16pt
                ],
                spacing: { after: 100 }
            }),
            new Paragraph({
                children: [
                    new TextRun({ text: `Vendor: ${vendorName || 'N/A'}`, bold: true, size: 32 }), // 16pt
                ],
                spacing: { after: 100 }
            }),
            new Paragraph({
                children: [
                    new TextRun({ text: `Date Generated: ${new Date().toLocaleDateString()}`, italics: true, color: "888888", size: 32 }), // 16pt
                ],
                spacing: { after: 400 } // More space after header
            })
        );

        // --- Loop through each section ---
        for (const section of sectionsData) {
            // Section Title
            docChildren.push(
                new Paragraph({
                    children: [ // TextRun for styling
                        new TextRun({
                            text: section.section_title,
                            size: 30, // 15pt
                            bold: true,
                            color: "005691",
                        }),
                    ],
                    heading: HeadingLevel.HEADING_1,
                    spacing: { before: 600, after: 200 },
                    alignment: AlignmentType.LEFT,
                    thematicBreak: true,
                })
            );

            // Section Description (if any)
            if (section.description) {
                docChildren.push(
                    new Paragraph({
                        children: [
                            new TextRun({
                                text: section.description,
                                size: 24, // 12pt
                                color: "808080",
                            }),
                        ],
                        indent: { left: 360 },
                        spacing: { after: 200 },
                    })
                );
            }

            // Prepare table rows for this section
            const tableRows: TableRow[] = [];

            // Add table header row
            tableRows.push(
                new TableRow({
                    children: [
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [new TextRun({ text: "Question", bold: true, size: 28, color: "005691" })] // 14pt
                                })
                            ],
                            verticalAlign: VerticalAlign.CENTER,
                            shading: { fill: "D9D9D9" },
                            margins: { top: 100, right: 100, bottom: 100, left: 100 } // Padding to header cell
                        }),
                        new TableCell({
                            children: [
                                new Paragraph({
                                    children: [new TextRun({ text: "Answer", bold: true, size: 28, color: "005691" })] // 14pt
                                })
                            ],
                            verticalAlign: VerticalAlign.CENTER,
                            shading: { fill: "D9D9D9" },
                            margins: { top: 100, right: 100, bottom: 100, left: 100 } // Padding to header cell
                        }),
                    ],
                    tableHeader: true,
                })
            );

            // --- Loop through each question in the section to populate table rows ---
            for (const question of section.questions) {
                const answer = this.commonService.getAnswerForQuestion(question.id, answersData);

                const questionContent = new Paragraph({
                    children: [
                        new TextRun({ text: `${question.question_text}:`, bold: true, size: 24 }) // 12pt
                    ],
                    spacing: { after: 100 }
                });

                const answerParagraphs: Paragraph[] = [];
                let defaultAnswerText = 'No answer provided.';
                let answerColor = "333333";

                if (answer) {
                    switch (question.question_type) {
                        case 'TEXT_SHORT':
                        case 'TEXT_LONG':
                        case 'NUMBER':
                        case 'CURRENCY':
                        case 'BOOLEAN_YES_NO':
                        case 'DATE':
                        case 'MULTIPLE_CHOICE_SINGLE':
                        case 'DROPDOWN':
                            let textAnswer: string;
                            if (question.question_type === 'DATE') {
                                textAnswer = answer.answer_date ? new Date(answer.answer_date).toLocaleDateString() : 'N/A';
                            } else if (question.question_type === 'CURRENCY') {
                                textAnswer = (answer.answer_number !== null && answer.answer_number !== undefined) ? `$${answer.answer_number.toFixed(2)}` : 'N/A';
                            } else if (question.question_type === 'BOOLEAN_YES_NO') {
                                textAnswer = answer.answer_boolean ? 'Yes' : 'No';
                            } else if (question.question_type === 'MULTIPLE_CHOICE_SINGLE' || question.question_type === 'DROPDOWN') {
                                textAnswer = this.commonService.getChoiceText(question.choices, answer.answer_selected_choice_ids);
                            } else if (question.question_type === 'NUMBER') {
                                textAnswer = (answer.answer_number !== null && answer.answer_number !== undefined) ? String(answer.answer_number) : 'N/A';
                            }
                            else {
                                textAnswer = answer.answer_text_long || answer.answer_text_short || defaultAnswerText;
                            }
                            answerParagraphs.push(
                                new Paragraph({
                                    children: [new TextRun({ text: textAnswer, color: answerColor, size: 22 })] // 11pt
                                })
                            );
                            break;

                        case 'FILE_UPLOAD':
                            if (answer.answer_file_references && answer.answer_file_references.length > 0) {
                                answerParagraphs.push(new Paragraph({
                                    children: [new TextRun({ text: 'Attached Files:', size: 22 })] // 11pt
                                }));
                                answer.answer_file_references.forEach((fileRef: any) => {
                                    answerParagraphs.push(new Paragraph({
                                        children: [
                                            new TextRun({ text: `- ${fileRef.file_name || 'Unnamed File'} `, size: 22 }), // 11pt
                                            new ExternalHyperlink({
                                                children: [
                                                    new TextRun({ text: `(Link)`, style: "Hyperlink", size: 22 }), // 11pt
                                                ],
                                                link: fileRef.file_url,
                                            }),
                                        ],
                                        indent: { left: 200 }
                                    }));
                                });
                            } else {
                                answerParagraphs.push(new Paragraph({
                                    children: [new TextRun({ text: 'No files attached.', size: 22, color: answerColor })] // 11pt
                                }));
                            }
                            break;

                        case 'MULTIPLE_CHOICE_MULTI':
                            if (answer.answer_selected_choice_ids && answer.answer_selected_choice_ids.length > 0) {
                                answerParagraphs.push(new Paragraph({
                                    children: [new TextRun({ text: 'Selected Choices:', size: 22 })] // 11pt
                                }));
                                answer.answer_selected_choice_ids.forEach((choiceId: any) => {
                                    answerParagraphs.push(new Paragraph({
                                        children: [
                                            new TextRun({ text: `- ${this.commonService.getChoiceText(question.choices, choiceId)}`, size: 22 }) // 11pt
                                        ],
                                        bullet: { level: 0 },
                                        indent: { left: 200 }
                                    }));
                                });
                            } else {
                                answerParagraphs.push(new Paragraph({
                                    children: [new TextRun({ text: 'No choices selected.', size: 22, color: answerColor })] // 11pt
                                }));
                            }
                            break;

                        default:
                            answerParagraphs.push(new Paragraph({
                                children: [new TextRun({ text: `Unsupported type: ${question.question_type}`, size: 22, color: "FF8C00" })] // 11pt
                            }));
                            break;
                    }
                } else {
                    answerParagraphs.push(new Paragraph({
                        children: [new TextRun({ text: defaultAnswerText, color: "FF0000", size: 22 })] // 11pt
                    }));
                }

                tableRows.push(
                    new TableRow({
                        children: [
                            new TableCell({
                                children: [questionContent],
                                verticalAlign: VerticalAlign.TOP,
                                borders: {
                                    top: { style: BorderStyle.SINGLE, size: 1 },
                                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                                    left: { style: BorderStyle.SINGLE, size: 1 },
                                    right: { style: BorderStyle.SINGLE, size: 1 },
                                },
                                margins: { top: 100, right: 100, bottom: 100, left: 100 } // Padding to question cell
                            }),
                            new TableCell({
                                children: answerParagraphs,
                                verticalAlign: VerticalAlign.TOP,
                                borders: {
                                    top: { style: BorderStyle.SINGLE, size: 1 },
                                    bottom: { style: BorderStyle.SINGLE, size: 1 },
                                    left: { style: BorderStyle.SINGLE, size: 1 },
                                    right: { style: BorderStyle.SINGLE, size: 1 },
                                },
                                margins: { top: 100, right: 100, bottom: 100, left: 100 } // Padding to answer cell
                            }),
                        ],
                    })
                );
            }

            // Add the table to the document children array after the section title/description
            if (tableRows.length > 1) {
                docChildren.push(
                    new Table({
                        rows: tableRows,
                        width: { size: 100, type: WidthType.PERCENTAGE },
                        borders: {
                            top: { style: BorderStyle.NONE },
                            bottom: { style: BorderStyle.NONE },
                            left: { style: BorderStyle.NONE },
                            right: { style: BorderStyle.NONE },
                            insideHorizontal: { style: BorderStyle.SINGLE, size: 1 },
                            insideVertical: { style: BorderStyle.SINGLE, size: 1 },
                        },
                        indent: { size: 100, type: WidthType.DXA }
                    })
                );
                docChildren.push(new Paragraph({ spacing: { after: 400 } }));
            }
        }

        // --- Concluding Section ---
        docChildren.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: 'End of Vendor Submission Report',
                        bold: true,
                        color: "005691",
                        size: 64, // 32pt
                    }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 800, after: 200 },
                heading: HeadingLevel.HEADING_1,
            }),
            new Paragraph({
                children: [
                    new TextRun({
                        text: 'Thank you for your submission.',
                        size: 36, // 18pt
                        color: "666666",
                    }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 800 },
            })
        );

        const doc = new Document({
            sections: [{
                properties: {
                    page: {
                        margin: {
                            top: 720,
                            right: 720,
                            bottom: 720,
                            left: 720,
                        },
                    },
                },
                children: docChildren,
            }],
        });

        // --- Write the file (Client-side download using file-saver) ---
        try {
            const blob = await Packer.toBlob(doc);
            saveAs(blob, `Vendor_Submission_${submissionId}.docx`);
            console.log("DOCX Generation Complete and downloaded.");
        } catch (error) {
            console.error("Error generating DOCX:", error);
        }
    }
}
